// =============================================================================
// NewVocModal — modal opened from EmployeeVocsScreen "+ New VOC"
// =============================================================================
// T-313 / T-252 Phase A. Step 2 of the user flow:
//   - Pick a template (auto-filters to active)
//   - Pick employee (defaults to the one on the EmployeeVocsScreen)
//   - Pick assessor (defaults to current user — manager+)
//   - Pick assessment date (defaults to today, flexible for back-dated paper)
// On confirm: calls app.create_voc_assessment RPC and returns the new id
// via onCreated callback (which routes to the assessment screen).
// =============================================================================
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { X, ShieldCheck, CalendarDays, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Field, Select, Input, Spinner } from "@/components/ui";

interface VocTemplate {
  voc_template_id: string;
  code:            string;
  title:           string;
  asset_kind:      string;
  asset_subtype:   string | null;
  version:         string;
  validity_years:  number;
}

interface EmployeePicker {
  employee_id:        string;
  first_name:         string | null;
  last_name:          string | null;
  primary_role_name:  string | null;
}

interface UserPicker {
  user_id:    string;
  role_name:  string | null;
  // Joined from employees via users.employee_id
  employee: { first_name: string | null; last_name: string | null } | null;
  login_email: string | null;   // fallback when user has no linked employee
}

function userDisplayName(u: UserPicker): string {
  if (u.employee) {
    const n = [u.employee.first_name, u.employee.last_name].filter(Boolean).join(" ").trim();
    if (n) return n;
  }
  return u.login_email ?? "Unnamed user";
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface Props {
  defaultEmployeeId: string;
  onClose:   () => void;
  onCreated: (newAssessmentId: string) => void;
}

export default function NewVocModal({ defaultEmployeeId, onClose, onCreated }: Props) {
  const user = useRequireAuth();

  const [templateId, setTemplateId] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<string>(defaultEmployeeId);
  const [assessorUserId, setAssessorUserId] = useState<string>(user?.user_id ?? "");
  const [assessedDate, setAssessedDate] = useState<string>(todayIso());
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (user?.user_id && !assessorUserId) setAssessorUserId(user.user_id); }, [user?.user_id, assessorUserId]);

  // Templates ----------------------------------------------------------------
  // T-313 / mig 161: voc_templates moved from app to public schema.
  const { data: templates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: ["voc_templates_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("voc_templates")
        .select("voc_template_id, code, title, asset_kind, asset_subtype, version, validity_years")
        .eq("is_active", true)
        .order("code");
      if (error) throw error;
      return (data ?? []) as VocTemplate[];
    },
  });

  // Employees ---------------------------------------------------------------
  const { data: employees = [] } = useQuery({
    queryKey: ["voc_employees_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as EmployeePicker[];
    },
  });

  // Assessor pool — manager+ users. T-313 hotfix: users table has no full_name
  // column (that's on the v_employee_names view + computed from employees).
  // Join employees via users.employee_id to get the display name.
  const { data: assessors = [] } = useQuery({
    queryKey: ["voc_assessors_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select(`
          user_id, role_name, login_email,
          employee:employee_id(first_name, last_name)
        `)
        .in("role_name", ["director", "manager"])
        .eq("is_active", true);
      if (error) throw error;
      return ((data ?? []) as unknown as UserPicker[])
        .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
    },
  });

  // Create RPC --------------------------------------------------------------
  const createMut = useMutation({
    mutationFn: async () => {
      if (!templateId) throw new Error("Pick a VOC template.");
      if (!employeeId) throw new Error("Pick an employee.");
      if (!assessorUserId) throw new Error("Pick an assessor.");
      if (!assessedDate) throw new Error("Pick the assessment date.");

      // Send full ISO timestamp so timezone is correct on the server side
      const assessedTs = new Date(assessedDate + "T09:00:00").toISOString();

      const { data, error } = await supabase
        .schema("app")
        .rpc("create_voc_assessment", {
          p_template_id:      templateId,
          p_employee_id:      employeeId,
          p_assessor_user_id: assessorUserId,
          p_assessed_at:      assessedTs,
        });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (newId) => {
      onCreated(newId);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Failed to create assessment"),
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-16 px-4 overflow-y-auto"
      onClick={createMut.isPending ? undefined : onClose}
    >
      <div
        className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 p-5 border-b border-line">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck size={18} className="text-accent flex-shrink-0" />
            <h2 className="text-[16px] font-semibold text-text">Start a VOC assessment</h2>
          </div>
          <button
            onClick={onClose}
            disabled={createMut.isPending}
            className="p-1 rounded hover:bg-ink-hover text-text-muted disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Template */}
          <Field label="VOC template *" hint="Pick the competency being assessed.">
            <Select
              value={templateId}
              onChange={e => setTemplateId(e.target.value)}
              disabled={loadingTemplates}
            >
              <option value="">Pick a template…</option>
              {templates.map(t => (
                <option key={t.voc_template_id} value={t.voc_template_id}>
                  {t.code} · {t.title}
                </option>
              ))}
            </Select>
          </Field>

          {/* Employee */}
          <Field label="Employee *" hint="The assessee. Defaults to the person whose profile you opened this from.">
            <div className="relative">
              <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
              <Select
                value={employeeId}
                onChange={e => setEmployeeId(e.target.value)}
                className="pl-9"
              >
                <option value="">Pick an employee…</option>
                {employees.map(e => {
                  const name = [e.first_name, e.last_name].filter(Boolean).join(" ") || "Unnamed";
                  return (
                    <option key={e.employee_id} value={e.employee_id}>
                      {name}{e.primary_role_name ? ` · ${e.primary_role_name}` : ""}
                    </option>
                  );
                })}
              </Select>
            </div>
          </Field>

          {/* Assessor */}
          <Field label="Assessor *" hint="Who's conducting the assessment. Manager+ only.">
            <Select value={assessorUserId} onChange={e => setAssessorUserId(e.target.value)}>
              <option value="">Pick an assessor…</option>
              {assessors.map(a => (
                <option key={a.user_id} value={a.user_id}>
                  {userDisplayName(a)}{a.role_name ? ` · ${a.role_name}` : ""}
                </option>
              ))}
            </Select>
          </Field>

          {/* Date */}
          <Field label="Assessment date *" hint="Defaults to today. Back-date if filing a paper assessment.">
            <div className="relative">
              <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
              <Input
                type="date"
                mono
                value={assessedDate}
                onChange={e => setAssessedDate(e.target.value)}
                max={todayIso()}
                className="pl-9"
              />
            </div>
          </Field>

          {err && (
            <div className="px-3 py-2 bg-danger/10 border border-danger/30 rounded text-[12px] text-danger flex items-center gap-1.5">
              <AlertTriangle size={12} />
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-line flex items-center justify-end gap-2 bg-ink-raised rounded-b-lg">
          <Button variant="secondary" onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
          <Button
            icon={CheckCircle2}
            onClick={() => { setErr(null); createMut.mutate(); }}
            disabled={createMut.isPending || !templateId || !employeeId || !assessorUserId || !assessedDate}
          >
            {createMut.isPending ? "Creating…" : "Start assessment →"}
          </Button>
        </div>
      </div>
    </div>
  );
}
